// 시공사 만들기 마법사 — 회사 정보(필수) → 규모(선택·건너뛰기) → 확인(약관 동의·처리방침 확인 → 만들기) → /app.

import { FormEvent, useEffect, useRef, useState, useTransition } from "react";
import { toast } from "sonner";

import {
  MONTHLY_SITES,
  MONTHLY_SITES_LABEL,
  SIGNUP_SOURCE_LABEL,
  SIGNUP_SOURCES,
  STAFF_SIZE_LABEL,
  STAFF_SIZES,
  WORK_TYPE_LABEL,
  WORK_TYPES,
} from "@buildpy/core/modules/onboarding/types";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { selfCreateOrg } from "@/api/actions/onboarding";
import { Link, navigateTo } from "@/lib/nav";
import { queryClient, VIEW_KEY } from "@/lib/query";
import { readOnboardingDraft, removeOnboardingDraft, writeOnboardingDraft } from "@/modules/onboarding/draft-storage";
import { FREE_LIMITS_LINE, PRIVACY_PATH, TERMS_PATH } from "@/modules/plan/copy";
import CompanyFields from "./company-fields";
import {
  companyErrorOf,
  draftTextOf,
  EMPTY_WIZARD,
  ORG_CREATE_FAILED_MESSAGE,
  orgCreateFormOf,
  type WizardStep,
  type WizardValues,
  wizardDraftOf,
  wizardReviewOf,
} from "./onboarding-wizard-state";
import SignupSteps from "./signup-steps";

const STEP_LABEL = { company: "회사 정보", size: "규모", review: "확인" } as const;

const LINK = "font-medium text-primary underline underline-offset-4 hover:text-foreground";

interface ChipsProps<T extends string> {
  label: string;
  options: readonly T[];
  labels: Record<T, string>;
  isOn: (value: T) => boolean;
  onPick: (value: T) => void;
  multi?: boolean;
  testId: string;
}

// 고르기 칩 — 하나만은 radio(aria-checked) · 여러 개는 토글(aria-pressed).
const Chips = <T extends string>({ label, options, labels, isOn, onPick, multi = false, testId }: ChipsProps<T>) => (
  <div className="flex flex-col gap-y-1.5">
    <span className="text-sm font-medium">{label}</span>
    <div aria-label={label} className="flex flex-wrap gap-1.5" data-testid={testId} role={multi ? "group" : "radiogroup"}>
      {options.map((value) => (
        <Button
          key={value}
          aria-checked={multi ? undefined : isOn(value)}
          aria-pressed={multi ? isOn(value) : undefined}
          data-value={value}
          role={multi ? undefined : "radio"}
          size="chip"
          type="button"
          variant="chip"
          onClick={() => onPick(value)}>
          {labels[value]}
        </Button>
      ))}
    </div>
  </div>
);

interface ViewProps {
  step: WizardStep;
  values: WizardValues;
  error: string | null;
  pending: boolean;
  onChange: (patch: Partial<WizardValues>) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onCreate: () => void;
}

export const OnboardingWizardView = ({ step, values: v, error, pending, onChange, onNext, onBack, onSkip, onCreate }: ViewProps) => {
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step === "review") onCreate();
    else onNext();
  };
  const review = step === "review" ? wizardReviewOf(v) : null;
  return (
    <form className="flex flex-col gap-y-6" data-step={step} data-testid="onboarding-wizard" noValidate onSubmit={submit}>
      <SignupSteps current={STEP_LABEL[step]} />

      {step === "company" && (
        <>
          <div className="flex flex-col gap-y-1">
            <h1 className="text-xl font-bold">회사 정보</h1>
            <p className="text-sm text-muted-foreground">견적서·계약서와 고객 화면에 그대로 들어갑니다. 설정에서 언제든 고칠 수 있습니다.</p>
          </div>
          <CompanyFields pending={pending} values={v} onChange={onChange} />
        </>
      )}

      {step === "size" && (
        <>
          <div className="flex flex-col gap-y-1">
            <h1 className="text-xl font-bold">회사 규모 · 선택</h1>
            <p className="text-sm text-muted-foreground">맞는 견적 템플릿과 시작 안내를 고르는 데 씁니다. 건너뛰어도 됩니다.</p>
          </div>
          <div className="flex flex-col gap-y-5">
            <Chips
              isOn={(x) => v.staffSize === x}
              label="직원 수"
              labels={STAFF_SIZE_LABEL}
              options={STAFF_SIZES}
              testId="onboarding-staff"
              onPick={(x) => onChange({ staffSize: v.staffSize === x ? null : x })}
            />
            <Chips
              isOn={(x) => v.workTypes.includes(x)}
              label="주로 하는 공사 · 여러 개 고를 수 있음"
              labels={WORK_TYPE_LABEL}
              multi
              options={WORK_TYPES}
              testId="onboarding-work"
              onPick={(x) => onChange({ workTypes: v.workTypes.includes(x) ? v.workTypes.filter((t) => t !== x) : [...v.workTypes, x] })}
            />
            <Chips
              isOn={(x) => v.monthlySites === x}
              label="한 달에 진행하는 현장"
              labels={MONTHLY_SITES_LABEL}
              options={MONTHLY_SITES}
              testId="onboarding-sites"
              onPick={(x) => onChange({ monthlySites: v.monthlySites === x ? null : x })}
            />
            <Chips
              isOn={(x) => v.source === x}
              label="buildpy를 알게 된 곳"
              labels={SIGNUP_SOURCE_LABEL}
              options={SIGNUP_SOURCES}
              testId="onboarding-source"
              onPick={(x) => onChange({ source: v.source === x ? null : x })}
            />
          </div>
        </>
      )}

      {review && (
        <>
          <div className="flex flex-col gap-y-1">
            <h1 className="text-xl font-bold">이대로 시공사를 만들까요?</h1>
            <p className="text-sm text-muted-foreground">만든 뒤에도 설정 › 회사 정보에서 모두 고칠 수 있습니다.</p>
          </div>
          <dl className="grid gap-2 text-sm sm:grid-cols-2" data-testid="onboarding-review">
            <div>
              <dt className="text-muted-foreground">상호</dt>
              <dd className="font-medium">{review.name}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">간편접수 주소</dt>
              <dd className="font-medium">/s/intake/{review.slug}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">대표자명</dt>
              <dd className="font-medium">{review.ceo}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">대표 전화</dt>
              <dd className="font-medium">{review.tel}</dd>
            </div>
          </dl>
          <div className="flex flex-col gap-y-1 rounded-md border border-primary/30 bg-primary/5 p-4 text-sm" data-testid="onboarding-free-plan">
            <p className="font-semibold">무료 요금제로 시작합니다</p>
            <ul className="flex flex-col gap-y-0.5 text-secondary-foreground">
              <li>현장·고객·견적·계약 수 제한 없음</li>
              <li>{FREE_LIMITS_LINE}</li>
              <li>한도를 넘어도 막지 않고 알려 드립니다</li>
            </ul>
            <p className="text-xs text-muted-foreground">무료 시공사는 계정 하나에 하나만 만들 수 있습니다.</p>
          </div>
          <div className="flex flex-col gap-y-3" data-testid="onboarding-consent">
            <label className="flex items-start gap-x-2 text-sm">
              <Checkbox
                aria-labelledby="onboarding-terms-label"
                checked={v.terms}
                className="mt-0.5"
                disabled={pending}
                id="onboarding-terms"
                name="terms"
                onCheckedChange={(c) => onChange({ terms: c === true })}
              />
              <span id="onboarding-terms-label">
                (필수){" "}
                <Link className={LINK} href={TERMS_PATH} rel="noopener noreferrer" target="_blank">
                  이용약관<span className="text-xs"> (새 창)</span>
                </Link>
                에 동의합니다
              </span>
            </label>
            <label className="flex items-start gap-x-2 text-sm">
              <Checkbox
                aria-labelledby="onboarding-privacy-label"
                checked={v.privacy}
                className="mt-0.5"
                disabled={pending}
                id="onboarding-privacy"
                name="privacy"
                onCheckedChange={(c) => onChange({ privacy: c === true })}
              />
              <span id="onboarding-privacy-label">
                (필수){" "}
                <Link className={LINK} href={PRIVACY_PATH} rel="noopener noreferrer" target="_blank">
                  개인정보 처리방침<span className="text-xs"> (새 창)</span>
                </Link>
                을 확인했습니다
              </span>
            </label>
          </div>
        </>
      )}

      {error && (
        <p className="text-sm text-destructive" data-testid="onboarding-error" role="alert">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        {step === "company" ? (
          <span />
        ) : (
          <Button disabled={pending} type="button" variant="ghost" onClick={onBack}>
            이전
          </Button>
        )}
        <div className="flex flex-wrap gap-2">
          {step === "size" && (
            <Button data-testid="onboarding-skip" disabled={pending} type="button" variant="outline" onClick={onSkip}>
              건너뛰기
            </Button>
          )}
          <Button data-testid="onboarding-next" disabled={pending || (step === "review" && !(v.terms && v.privacy))} type="submit">
            {step === "review" ? (pending ? "만드는 중…" : "시공사 만들고 시작") : "다음"}
          </Button>
        </div>
      </div>
    </form>
  );
};

const NEXT: Record<WizardStep, WizardStep> = { company: "size", size: "review", review: "review" };
const PREV: Record<WizardStep, WizardStep> = { company: "company", size: "company", review: "size" };

// 상태를 들고 액션을 부른다 — 입력은 사용자별 탭 초안(sessionStorage)으로 이어 가고, 만들고 나면 초안·이전 계정(조직 없음) 읽기 캐시를 버리고 /app 으로.
const OnboardingWizard = ({ userId }: { userId: string }) => {
  const [initial] = useState(() => wizardDraftOf(readOnboardingDraft(userId)));
  const [step, setStep] = useState<WizardStep>(initial?.step ?? "company");
  const [values, setValues] = useState<WizardValues>(initial?.values ?? EMPTY_WIZARD);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const done = useRef(false);

  useEffect(() => {
    if (!done.current) writeOnboardingDraft(userId, draftTextOf({ step, values }));
  }, [step, values, userId]);

  const onNext = () => {
    if (step === "company") {
      const problem = companyErrorOf(values);
      setError(problem);
      if (problem) return;
    }
    setError(null);
    setStep(NEXT[step]);
  };

  const onCreate = () =>
    startTransition(async () => {
      try {
        const result = await selfCreateOrg(orgCreateFormOf(values));
        if (!result.ok) {
          setError(result.message);
          return;
        }
        done.current = true;
        removeOnboardingDraft(userId);
        toast.success(result.message);
        queryClient.removeQueries({ queryKey: [VIEW_KEY] });
        navigateTo("/app", true);
      } catch {
        setError(ORG_CREATE_FAILED_MESSAGE);
      }
    });

  return (
    <OnboardingWizardView
      error={error}
      pending={pending}
      step={step}
      values={values}
      onBack={() => {
        setError(null);
        setStep(PREV[step]);
      }}
      onChange={(patch) => setValues((prev) => ({ ...prev, ...patch }))}
      onCreate={onCreate}
      onNext={onNext}
      onSkip={() => {
        setValues((prev) => ({ ...prev, staffSize: null, workTypes: [], monthlySites: null, source: null }));
        setError(null);
        setStep("review");
      }}
    />
  );
};

export default OnboardingWizard;
